// SSO callback state to reduce aggressive requests during authentication
let ssoInProgress = false;
let ssoTimestamp = 0;

export const setSSOInProgress = (inProgress: boolean) => {
  ssoInProgress = inProgress;
  ssoTimestamp = Date.now();
};

// Utility function to make authenticated API requests with fallback for Firefox
export const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  // In development, use relative URLs to leverage Vite's proxy
  // In production, build full URL using VITE_API_URL
  const isDevelopment = import.meta.env.DEV;
  const fullUrl = isDevelopment ? url : (url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL}${url}`);
  
  // Get JWT token from localStorage
  const jwtToken = localStorage.getItem('jwt_token');
  
  // For debugging only - log auth state without showing full token
  const authDebug = jwtToken ? 
    `JWT present (${jwtToken.substring(0, 6)}...)` : 
    'No JWT in localStorage';
  console.log(`[API] ${url.split('/').pop()} - Auth state: ${authDebug}`);
  
  // If SSO is in progress and recent, prioritize JWT for auth endpoints
  const ssoRecent = ssoInProgress && (Date.now() - ssoTimestamp < 5000); // 5 seconds
  const isAuthEndpoint = url.includes('/auth/') || url.includes('/subscription');
  
  if (ssoRecent && isAuthEndpoint && jwtToken) {
    // Skip cookie attempt during SSO, go straight to JWT
    try {
      const tokenResponse = await fetch(fullUrl, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          ...options.headers,
        },
      });
      
      if (tokenResponse.ok) {
        console.log(`[API] SSO-optimized JWT auth succeeded for ${url.split('/').pop()}`);
        return tokenResponse;
      }
    } catch (error) {
      // Fall through to normal flow
    }
  }
  
  // Always try cookies first with credentials
  try {
    const cookieResponse = await fetch(fullUrl, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    // If cookies work, return the response
    if (cookieResponse.ok) {
      return cookieResponse;
    }
    
    // If cookies fail but return a 401, log it for debugging
    if (cookieResponse.status === 401) {
      console.warn(`[API] Cookie auth failed with 401 for ${url.split('/').pop()}, trying JWT fallback`);
    }
    
    // If cookies fail and we have JWT token, try with JWT as fallback
    if (jwtToken) {
      const tokenResponse = await fetch(fullUrl, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          ...options.headers,
        },
      });
      
      // Log JWT results for debugging
      if (tokenResponse.ok) {
        console.log(`[API] JWT auth succeeded for ${url.split('/').pop()}`);
      } else if (tokenResponse.status === 401) {
        console.warn(`[API] JWT auth failed with 401 for ${url.split('/').pop()}, token may be invalid/expired`);
      }
      
      return tokenResponse;
    }
    
    // If no token available, return the original cookie response with detailed error
    if (cookieResponse.status === 401) {
      const error = new Error(`Authentication failed (401) for ${fullUrl} - No valid auth method found`);
      console.error(error);
      throw error;
    }
    
    return cookieResponse;
  } catch (error) {
    // If cookies fail completely and we have JWT token, try with JWT
    if (jwtToken) {
      try {
        const tokenResponse = await fetch(fullUrl, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`,
            ...options.headers,
          },
        });
        return tokenResponse;
      } catch (jwtError) {
        console.error(`[API] Both cookie and JWT auth failed for ${url}:`, jwtError);
        throw new Error(`Failed to fetch ${url}: ${jwtError instanceof Error ? jwtError.message : 'Unknown error'}`);
      }
    }
    
    // Re-throw the error with more context if no fallback available
    console.error(`[API] Request failed for ${url}:`, error);
    throw new Error(`Failed to fetch ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};